import { formatCents, formatCount, formatDecimal, parseMoneyToCents, tt } from '@agos/shared'

/**
 * §9.5's per-product VAT picker — *"per-product VAT picker + surcharge attachment in product editor"*.
 *
 * **What assigning a category actually does.** `OrderCreationService.vat` is two lines long and decides the
 * tax on every line of every order:
 *
 * ```java
 * return vatCategories.forProduct(productId)
 *     .map(category -> new VatRate(category.rate(), category.letter()))
 *     .orElseGet(() -> { BigDecimal rate = orderLevelRate(channel); … });
 * ```
 *
 * A product with a category carries **that** rate onto its line, snapshotted. A product without one falls
 * back to the order-level rate — the register's dine-in or delivery setting, then its default, then the
 * statutory 5/19 pair. So this picker is not a label: it changes what tax is charged.
 *
 * **FB-184.** Two facts that are harmless apart and dangerous together.
 *
 * 1. **No product uses the feature.** All 173 carry `vat_rate_dine_in` and `vat_rate_delivery`, and
 *    **zero** have a `vat_category_id` — so every line today takes the order-level fallback and the four
 *    categories §10A.3 configured are attached to nothing.
 * 2. **Two of the four categories cannot be printed** (FB-183): `C` 23 % and `D` 0 % have no confirmed
 *    register index, and `FiskalReceiptService` refuses a receipt carrying one.
 *
 * Together: **assigning an unprintable category to a product makes that product unsellable.** Not
 * *mispriced* — the order is taken, and then the receipt will not print, at the till, with the customer
 * waiting. Nothing in the backend stops it, because the assignment endpoint has no reason to know about
 * the register.
 *
 * So the picker refuses an unprintable category outright rather than warning. This is the one place in this
 * port where a warning is not enough: the person choosing is a manager editing a menu, and the person who
 * finds out is a cashier three days later.
 *
 * **And the current assignment cannot be read back.** `PUT /products/{id}/vat-category` writes
 * `products.vat_category_id`, and **nothing projects it**: `GET /products` answers `vatRateDineIn` and
 * `vatRateDelivery` and no category, and there is no `GET /products/{id}` at all — it answers *Request
 * method 'GET' is not supported*. So this editor knows what it just set and not what is set.
 *
 * That is a correctness problem before it is a display one. Sending the picker's value on every save
 * would mean an editor opened to change a **price** posts `vatCategoryId: null` alongside it and silently
 * strips a rate somebody chose last week — the product keeps selling, at the register's fallback rate,
 * and nothing anywhere says so. So the assignment is sent **only when the picker was touched**
 * ({@link shouldPersist}), and the field says it cannot show the current value rather than showing an
 * empty box that reads as *none*. Filed for the backend.
 */

export interface VatChoice {
  id: number
  name: string
  letter: string
  ratePercent: number
  printable: boolean
  isActive: boolean
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function vatChoicesOf(payload: unknown): VatChoice[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    const rate = row.rate
    return [
      {
        id: row.id,
        name: text(row.name) ?? '',
        letter: text(row.letter) ?? '',
        ratePercent: typeof rate === 'number' ? rate : Number(text(rate) ?? '0') || 0,
        // FB-183: no confirmed register index means no receipt.
        printable: typeof row.fiskalSlot === 'number',
        isActive: row.isActive === true,
      },
    ]
  })
}

/** The categories a product may be given: switched on, and printable. */
export function assignableChoices(choices: readonly VatChoice[]): VatChoice[] {
  return choices.filter((choice) => choice.isActive && choice.printable)
}

/** The ones deliberately withheld, and why — because silence here reads as *we only have two rates*. */
export function withheldChoices(choices: readonly VatChoice[]): VatChoice[] {
  return choices.filter((choice) => choice.isActive && !choice.printable)
}

export function withheldNote(choices: readonly VatChoice[]): string {
  const withheld = withheldChoices(choices)
  if (withheld.length === 0) return ''
  const named = withheld
    .map((choice) => `${choice.letter} (${formatDecimal(choice.ratePercent, 2)} %)`)
    .join(', ')
  return `${formatCount(withheld.length)} ${tt('sadzby sa tu neponúkajú')}: ${named}. ${tt('Pokladňa pre ne nemá potvrdený index, takže doklad s takým produktom by sa nevytlačil - nastavte index v Sadzbách DPH.')}`
}

export function choiceLabel(choice: VatChoice): string {
  return `${choice.letter} - ${formatDecimal(choice.ratePercent, 2)} % · ${choice.name}`
}

/**
 * What a product's line will actually be taxed at.
 *
 * With no category the rate is the order-level fallback, which **depends on the channel** — dine-in and
 * delivery are different rates for the same pizza, which is the whole reason the fallback exists. So the
 * honest answer for an unassigned product is not a number.
 */
export function effectiveRateNote(assigned: VatChoice | null): string {
  if (assigned !== null) {
    return `${tt('Evidenčná sadzba na riadku objednávky bude')} ${formatDecimal(assigned.ratePercent, 2)} % (${assigned.letter}), ${tt('bez ohľadu na kanál. Na doklade sa účtuje podľa dvoch sadzieb nižšie.')}`
  }
  return tt(
    'Bez sadzby sa evidenčná sadzba prevezme z nastavenia pokladne podľa kanála. Na doklade sa účtuje podľa dvoch sadzieb nižšie.',
  )
}

/**
 * The warning that belongs on the click that creates a disagreement — §9.280, FB-438.
 *
 * The receipt charges per channel from the two rates below the picker; the **recorded** rate on the
 * order line follows the category and is channel-blind. Today they never disagree, because no product
 * carries a category and the fallback is channel-aware too. Assigning one is what changes that, and on
 * this data it would change it for **130 of 173 products** — the ones whose two rates differ.
 *
 * Said only when the two would actually part company: a product at 23/23 loses nothing by carrying a
 * category, and warning about it would be noise.
 */
export function divergenceNote(
  assigned: VatChoice | null,
  dineInRate: string,
  deliveryRate: string,
): string {
  if (assigned === null) return ''
  if (dineInRate.trim() === '' || deliveryRate.trim() === '') return ''
  if (dineInRate.trim() === deliveryRate.trim()) return ''
  return tt(
    'Pozor: tento produkt má inú sadzbu na mieste a inú pri rozvoze. Doklad bude účtovať podľa nich, evidenčná sadzba na objednávke podľa vybranej sadzby - tie dve sa potom líšia.',
  )
}

export function assignedChoice(
  vatCategoryId: number | null,
  choices: readonly VatChoice[],
): VatChoice | null {
  if (vatCategoryId === null) return null
  return choices.find((choice) => choice.id === vatCategoryId) ?? null
}

/**
 * Whether the product is currently on a category that cannot be printed.
 *
 * Reachable through the API or through SQL even though this picker will not offer it, so the editor has to
 * be able to say so about a product it did not assign.
 */
export function isOnUnprintable(
  vatCategoryId: number | null,
  choices: readonly VatChoice[],
): boolean {
  const assigned = assignedChoice(vatCategoryId, choices)
  return assigned !== null && !assigned.printable
}

export function unprintableAssignmentWarning(assigned: VatChoice | null): string {
  if (assigned === null || assigned.printable) return ''
  return `${tt('Tento produkt má sadzbu')} ${assigned.letter} (${formatDecimal(assigned.ratePercent, 2)} %), ${tt('pre ktorú pokladňa nemá index - doklad s ním sa nevytlačí a zistí sa to až pri predaji. Doplňte index v Sadzbách DPH alebo sadzbu odoberte.')}`
}

/**
 * Whether to write the assignment at all.
 *
 * Only when somebody touched the picker. An untouched editor cannot know the current category — nothing
 * projects it — so posting its blank state would clear a rate it never saw.
 */
export function shouldPersist(touched: boolean): boolean {
  return touched
}

/**
 * What the editor says about the rate in force **when nothing is assigned**.
 *
 * `vatCategoryId` is projected now (`MIGRATION-STATUS` §10A.3, 2026-08-02), so the picker shows the
 * current assignment rather than admitting it cannot. This is what is said when there is none —
 * measured, **0 of 173** products carry one, so the register's fallback really is what every line
 * takes today.
 */
export function unassignedNote(): string {
  // §9.280 — this said *"účtuje sa sadzbou pokladne"*, which is the same wrong claim the other two
  // notes made: the doklad charges from the two per-channel rates below, whatever this picker holds.
  return tt(
    'Tento produkt nemá priradenú DPH kategóriu - evidenčná sadzba sa prevezme z pokladne. Na doklade sa účtuje podľa dvoch sadzieb nižšie.',
  )
}

/** The payload `POST /products/{id}/vat-category` takes. `null` clears it back to the fallback. */
export function assignmentPayload(vatCategoryId: number | null): Record<string, unknown> {
  return { vatCategoryId }
}

// ─── §9.5's surcharges, attached to this product ─────────────────────────────

/**
 * Deposits and per-item surcharges on a product — `PUT /products/{id}/surcharges/{surchargeId}`.
 *
 * `SurchargeService.forProduct` joins `product_surcharges` when an order is created, so an attached
 * surcharge becomes its own line on the receipt, multiplied by the quantity that carried it. Attaching
 * one is `{main_admin, admin}`; **creating** one is `main_admin`, on `/surcharges`.
 *
 * **The same read gap as the VAT category, one step worse.** Nothing projects `product_surcharges`
 * either — not the product list, and there is no product detail endpoint — so this editor cannot show
 * which surcharges a product already carries. A checkbox would therefore claim *not attached* about every
 * product, and unticking one that was never ticked would post a detach nobody asked for.
 *
 * So this is not a checkbox. It is a pair of explicit actions per surcharge, each of which does exactly
 * what it says and nothing on save, with a line saying the current state is not visible. An idempotent
 * `PUT` makes that safe: attaching twice is attaching.
 */
export interface SurchargeChoice {
  id: number
  name: string
  amountCents: number
  isActive: boolean
}

export function surchargeChoicesOf(payload: unknown): SurchargeChoice[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        name: text(row.name) ?? '',
        amountCents: parseMoneyToCents(row.amount as string | number | null),
        isActive: row.isActive !== false,
      },
    ]
  })
}

/** Only the ones that would actually be charged — `forProduct` filters on `s.is_active`. */
export function attachableSurcharges(choices: readonly SurchargeChoice[]): SurchargeChoice[] {
  return choices.filter((choice) => choice.isActive)
}

export function surchargeLabel(choice: SurchargeChoice): string {
  return `${choice.name} · ${formatCents(choice.amountCents)}`
}

/** The body the attach endpoint reads. Absent or anything but `false` attaches. */
export function attachPayload(attached: boolean): Record<string, unknown> {
  return { attached }
}

/**
 * The attached set after a write that has already succeeded — FB-199.
 *
 * <p>Until 2026-08-11 the rows read `product.surchargeIds` straight off the prop and the mutation's
 * `onSuccess` fired only a toast. The write landed, the row went on saying *Priradiť*, and reopening
 * the dialog re-took the same stale list — so a deposit could be attached all day and the one screen
 * that shows it never agreed. The toast made it worse than silence: the screen announced the change
 * and displayed its opposite.
 *
 * <p>Idempotent on purpose. The buttons write immediately and a second press of *Priradiť* is a
 * repeat, not a toggle — the caller knows which direction it asked for, so this applies that
 * direction rather than flipping whatever is there.
 */
export function withAttachment(
  ids: readonly number[],
  surchargeId: number,
  attached: boolean,
): number[] {
  const without = ids.filter((id) => id !== surchargeId)
  return attached ? [...without, surchargeId] : without
}

/**
 * Said under the surcharge rows.
 *
 * The server projects `surchargeIds` now, so each row shows whether it is attached — what remains
 * worth saying is that the buttons write **immediately**, which is the surprising half: everything
 * else in this dialog waits for *Uložiť*.
 */
export function surchargeStateNote(): string {
  return tt('Priradenie sa uloží hneď, nie až pri uložení produktu.')
}

/** Said where there is nothing to attach, because zero here has a specific cause. */
export function noSurchargesNote(): string {
  return tt('Zatiaľ nie sú založené žiadne príplatky - založíte ich v Príplatkoch a zálohách.')
}
